import React, { useState, useEffect, useCallback } from 'react';
import { createPlaceholderImage } from '../../utils/imageUtils';
import './EnergeticCityPage.css';

interface EnergeticCityPageProps {
  isVisible: boolean;
}

interface SlideImage {
  id: number;
  src: string;
  alt: string;
  title: string;
}

const EnergeticCityPage: React.FC<EnergeticCityPageProps> = ({ isVisible }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [images, setImages] = useState<SlideImage[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  // 仮画像を生成
  useEffect(() => {
    const placeholderImages = [
      {
        id: 1,
        src: createPlaceholderImage(800, 600, 'VRゴーグル', '#1a1a2e'),
        alt: 'VRゴーグル',
        title: 'VRゴーグル'
      },
      {
        id: 2,
        src: createPlaceholderImage(800, 600, '清水寺', '#16213e'),
        alt: '清水寺',
        title: '清水寺'
      },
      {
        id: 3,
        src: createPlaceholderImage(800, 600, '東京の町', '#0f3460'),
        alt: '東京の町',
        title: '東京の町'
      }
    ];
    setImages(placeholderImages);
    setIsLoaded(true);
  }, []);

  // 自動スライド切り替え
  useEffect(() => {
    if (!isVisible || !isLoaded) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % images.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isVisible, isLoaded, images.length]);

  const goToSlide = useCallback((index: number) => {
    setCurrentSlide(index);
  }, []);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  if (!isLoaded || images.length === 0) {
    return null;
  }

  return (
    <div className={`energetic-city-container ${isVisible ? 'visible' : ''}`}>
      {/* ページタイトル */}
      <div className="city-title-container">
        <h2 className="city-title">元気な街</h2>
      </div>

      {/* スライドショー */}
      <div className="slideshow-container">
        <div className="slides-wrapper">
          {images.map((image, index) => (
            <div
              key={image.id}
              className={`slide ${index === currentSlide ? 'active' : ''}`}
            >
              <img
                src={image.src}
                alt={image.alt}
                className="slide-image"
                onContextMenu={(e) => e.preventDefault()}
                draggable={false}
              />
              <div className="slide-overlay">
                <h3 className="slide-title">{image.title}</h3>
              </div>
            </div>
          ))}
        </div>

        {/* ナビゲーションボタン */}
        <button
          className="slide-nav prev"
          onClick={prevSlide}
          aria-label="前の画像"
        >
          <span>&lt;</span>
        </button>
        <button
          className="slide-nav next"
          onClick={nextSlide}
          aria-label="次の画像"
        >
          <span>&gt;</span>
        </button>

        {/* ドットインジケーター */}
        <div className="slide-indicators">
          {images.map((_, index) => (
            <button
              key={index}
              className={`indicator ${index === currentSlide ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`画像 ${index + 1} へ移動`}
            />
          ))}
        </div>
      </div>

      {/* 進行状況バー */}
      <div className="progress-container">
        <div 
          className="progress-bar"
          style={{ 
            width: `${((currentSlide + 1) / images.length) * 100}%` 
          }}
        />
      </div>
    </div>
  );
};

export default EnergeticCityPage;